import React from 'react';

/**
 * Renders a single config-driven custom field control.
 * Used by QA/QC Phase 3/4; respects field.permissions when present.
 */
const DynamicFieldRenderer = ({
  field,
  value,
  onChange,
  disabled = false,
}) => {
  if (!field || field.hidden) return null;
  if (field.permissions && field.permissions.canView === false) return null;

  const id = `dyn-field-${field.fieldKey}`;
  const label = field.label || field.fieldKey;
  const required = Boolean(field.required);
  const permReadOnly = field.permissions
    ? (field.readOnly || !(field.permissions.canCreate || field.permissions.canEdit))
    : Boolean(field.readOnly);
  const readOnly = Boolean(field.readOnly) || permReadOnly || disabled;
  const help = field.helpText;
  const placeholder = field.placeholder || '';

  const setVal = (next) => {
    if (typeof onChange === 'function') onChange(field.fieldKey, next);
  };

  let control = null;
  switch (field.fieldType) {
    case 'textarea':
      control = (
        <textarea
          id={id}
          rows={2}
          placeholder={placeholder}
          value={value ?? ''}
          disabled={readOnly}
          onChange={(e) => setVal(e.target.value)}
        />
      );
      break;
    case 'number':
      control = (
        <input
          id={id}
          type="number"
          placeholder={placeholder}
          value={value ?? ''}
          disabled={readOnly}
          onChange={(e) => setVal(e.target.value === '' ? '' : e.target.value)}
        />
      );
      break;
    case 'date':
      control = (
        <input
          id={id}
          type="date"
          value={value ?? ''}
          disabled={readOnly}
          onChange={(e) => setVal(e.target.value)}
        />
      );
      break;
    case 'boolean':
      control = (
        <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <input
            id={id}
            type="checkbox"
            checked={Boolean(value)}
            disabled={readOnly}
            onChange={(e) => setVal(e.target.checked)}
          />
          <span>{placeholder || 'Yes'}</span>
        </label>
      );
      break;
    case 'select': {
      const options = Array.isArray(field.options) ? field.options : [];
      control = (
        <select
          id={id}
          value={value ?? ''}
          disabled={readOnly}
          onChange={(e) => setVal(e.target.value)}
        >
          <option value="">Select</option>
          {options.map((o) => (
            <option key={o.optionKey || o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
      break;
    }
    case 'email':
      control = (
        <input
          id={id}
          type="email"
          placeholder={placeholder}
          value={value ?? ''}
          disabled={readOnly}
          onChange={(e) => setVal(e.target.value)}
        />
      );
      break;
    case 'text':
    default:
      control = (
        <input
          id={id}
          type="text"
          placeholder={placeholder}
          value={value ?? ''}
          disabled={readOnly}
          onChange={(e) => setVal(e.target.value)}
        />
      );
  }

  return (
    <div className="form-group" data-field-key={field.fieldKey} data-field-class={field.fieldClass || 'CUSTOM'}>
      {field.fieldType !== 'boolean' && (
        <label htmlFor={id}>
          {label}{required ? <span className="req"> *</span> : null}
        </label>
      )}
      {field.fieldType === 'boolean' && (
        <label style={{ display: 'block', marginBottom: 4 }}>
          {label}{required ? <span className="req"> *</span> : null}
        </label>
      )}
      {control}
      {help ? <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{help}</div> : null}
      {readOnly && field.permissions && !disabled ? (
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Read-only for your role</div>
      ) : null}
    </div>
  );
};

export const DynamicFieldsSection = ({
  fields = [],
  values = {},
  onChange,
  disabled = false,
  title = 'Additional fields',
}) => {
  const visible = (fields || []).filter((f) => f && f.fieldClass === 'CUSTOM' && !f.hidden);
  if (!visible.length) return null;
  return (
    <div className="dynamic-fields-section" style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #e2e8f0' }}>
      <p style={{ fontSize: 12, fontWeight: 600, color: '#475569', margin: '0 0 8px' }}>{title}</p>
      {visible.map((f) => (
        <DynamicFieldRenderer
          key={f.fieldKey}
          field={f}
          value={values[f.fieldKey]}
          onChange={onChange}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

export default DynamicFieldRenderer;
